/**
 * Troca obrigatória de senha temporária. (BKC-002)
 *
 * Para onde o roteador leva quem entrou com senha definida por administrador. Não
 * é uma sugestão: a API recusa qualquer outra rota com `PASSWORD_CHANGE_REQUIRED`
 * enquanto a troca não acontecer, e esta tela é a única saída — junto com sair.
 *
 * Deliberadamente sem navegação lateral: oferecer os menus aqui prometeria acesso
 * que a API vai negar, e a pessoa concluiria que o sistema está quebrado.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRANDING } from '../config/branding';
import { SimboloCeres } from '../components/Marca';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { t } from '../lib/i18n';
import {
  Field,
  FormErrorSummary,
  InlineError,
  Spinner,
  useToast,
} from '../components/ui';

export function ChangePasswordPage(): JSX.Element {
  const { user, refresh, logout } = useSession();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const divergem = confirmation !== '' && newPassword !== confirmation;
  const igualAtual = newPassword !== '' && newPassword === currentPassword;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/password', { currentPassword, newPassword });

      // A troca revoga TODAS as sessões, inclusive esta — por isso o caminho é
      // entrar de novo, e não seguir navegando com um cookie morto.
      notify('success', 'Senha alterada. Entre novamente com a sua nova senha.');
      await logout();
      navigate('/entrar', { replace: true });
    } catch (err) {
      setError(err);
      // Uma falha aqui pode ser sessão expirada: revalida para o roteador decidir.
      if (err instanceof ApiError && err.isUnauthenticated) await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken p-4">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center gap-2.5">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <div>
            <p className="text-base font-semibold text-ink">{BRANDING.productName}</p>
            <p className="text-xs text-ink-subtle">{BRANDING.tagline}</p>
          </div>
        </div>

        <div className="panel px-5 py-5">
          <h1 className="text-lg font-semibold text-ink">{t('trocarSenha.titulo')}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t('trocarSenha.explicacao')}</p>
          {user !== null && (
            <p className="mt-1 text-xs text-ink-subtle">Entrando como {user.email}</p>
          )}

          <form className="mt-4 space-y-3" noValidate onSubmit={(e) => void submit(e)}>
            <Field label={t('trocarSenha.atual')} htmlFor="atual" required>
              <input
                id="atual"
                type="password"
                className="field-input"
                autoComplete="current-password"
                autoFocus
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </Field>

            <Field
              label={t('trocarSenha.nova')}
              htmlFor="nova"
              required
              hint="Ao menos 10 caracteres. Prefira uma frase longa e fácil de lembrar."
              error={igualAtual ? 'A nova senha deve ser diferente da atual.' : undefined}
            >
              <input
                id="nova"
                type="password"
                className="field-input"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>

            <Field
              label={t('trocarSenha.repita')}
              htmlFor="repetir"
              required
              error={divergem ? 'As senhas não coincidem.' : undefined}
            >
              <input
                id="repetir"
                type="password"
                className="field-input"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>

            <FormErrorSummary error={error} />
            <InlineError error={error} />

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={
                busy ||
                currentPassword === '' ||
                newPassword === '' ||
                divergem ||
                igualAtual
              }
            >
              {busy && <Spinner />}
              Trocar senha e continuar
            </button>
          </form>

          <button
            type="button"
            className="mt-3 w-full text-sm text-ink-muted hover:text-ink hover:underline"
            onClick={() => {
              void logout().then(() => navigate('/entrar', { replace: true }));
            }}
          >{t('trocarSenha.sair')}</button>
        </div>
      </div>
    </div>
  );
}
